/**
 * Virtual twin-stick (DECISIONS.md AMD-1 — touch is the primary input path).
 *
 * ## Roles go by arrival order, never by screen region (AMD-1 amended)
 *
 * Playtest, verbatim: *"The joystick should work on any zone of the screen, not just on the left."*
 * The half-split is gone from **both** schemes and one rule covers both:
 *
 * - **First pointer down, anywhere** → movement. In `stick` that is the dynamic-origin stick;
 *   in `follow` it is the follow pointer (`follow.ts`).
 * - **Second concurrent pointer, anywhere** → Focus Aim, an origin→current drag on `aimStick`.
 * - **Third and later** → dropped.
 *
 * This is the same reasoning that already killed the half-split in `follow`: a centred ship under a
 * left-half restriction can never be told to move right, and a stick pinned to one half is a stick
 * the player cannot put where the thumb already is. Nothing here reads `rectWidth`.
 *
 * Ownership binds at touch-down and is keyed on `pointerId`, never on index or arrival index:
 * a pointer keeps the role it was given until it lifts, a thumb that slides anywhere keeps its
 * stick, and lifting the movement finger frees the movement slot **without** promoting the aim
 * finger mid-drag. The freed slot goes to the next finger down.
 *
 * ## Dash is a double-tap
 *
 * Playtest, verbatim: *"Flash isn't really needed."* — so dash stops being a button and stops being
 * a single tap, which under arrival order would fire every time a thumb was re-placed.
 *
 * A **tap** is unchanged from AMD-1: release within `tapTicks` (§3.6, 180 ms → 11 ticks) of its own
 * touch-down, having travelled under `tapSlop` (12 px). A **double-tap** is a tap followed by
 * another touch-down that lands within `doubleTapTicks` of the first tap's *release* and within
 * `tapSlop × 2` of the first tap's *touch-down point*. Dash latches on that second touch-down.
 *
 * Three properties fall out of "on the second down" rather than "on the second release":
 *
 * - Dash is not delayed by a second tap's worth of wall clock, so §3.11's ≤ 1 tick still holds from
 *   the gesture's committing event.
 * - Double-tap-and-hold works: the second contact is also the new movement stick, so the player
 *   dashes and keeps steering out of it in one gesture.
 * - A lone drag can never be a dash — there is no preceding tap to pair with. A double-tap cannot
 *   be swallowed by a drag either, because the pair is already decided before the second contact
 *   has moved at all.
 *
 * Both fingers can do it: any pointer's tap arms the pair and any pointer's touch-down closes it.
 * Contacts get fresh `pointerId`s by definition, so the spatial gate is what stands in for
 * "the same finger". The pair is consumed when it fires, so three taps are one dash, not two.
 *
 * §3.4 takes the dash direction from the live move input, which on the second touch-down is still
 * the heading the ship was already carrying (`rt.headingX/Y` in `sim/player.ts`) — a double-tap
 * dashes the way you were going, and is never directionless.
 */

import { FollowPointer, type FollowView, type MovementScheme } from './follow';

/** Travel radius of the virtual stick, CSS px (AMD-1). */
export const STICK_RADIUS_PX = 44;
/** §3.6 tap window, expressed in sim ticks (180 ms ÷ 16.667 ms). */
export const TAP_TICKS = 11;
/** Tap travel ceiling, CSS px. */
export const TAP_TRAVEL_PX = 12;

/**
 * Gap between a tap's release and the next touch-down that still reads as one double-tap, ms.
 * The registry entry `input.double_tap_ms` is the designer knob; this is its default.
 *
 * Measured release→down rather than down→down, because the gap is the thing the player performs and
 * the tap durations either side of it are already bounded by `tap_ms`. 200 ms puts the equivalent
 * down→down span at 200 + the first tap's own length: 283 ms for a typical 5-tick tap and 383 ms in
 * the worst case, straddling the ~300 ms every touch platform settled on (Android's
 * `ViewConfiguration` double-tap timeout is exactly 300 ms).
 *
 * It is deliberately *not* the §3.6 dash buffer's 150 ms / 9 ticks. That window answers a different
 * question — how long an already-pressed dash stays legal while the sim refuses it — and welding
 * the two would mean a designer loosening dash forgiveness silently retuning the gesture. It is
 * also deliberately longer than `tap_ms`'s 11 ticks: a tap's ceiling is a duration, the gap is a
 * rhythm, and the rhythm has to be at least as generous as the beats it separates or a player
 * tapping at the edge of the tap window could never close a pair.
 */
export const DOUBLE_TAP_MS = 200;
/** `DOUBLE_TAP_MS` in sim ticks (200 ms ÷ 16.667 ms). §3.6: buffers count ticks, not wall clock. */
export const DOUBLE_TAP_TICKS = 12;
/**
 * How far the second touch-down may land from the first tap's touch-down and still pair, as a
 * multiple of `tap_slop`. One tap may wander a full `tap_slop`, so two taps of one gesture can sit
 * `2 × tap_slop` apart at their down points from nothing but the thumb re-landing. At the default
 * that is 24 px — comfortably under the 44 px stick radius, so a pair can never span what the
 * player would read as deliberately re-placing the stick somewhere else.
 */
export const DOUBLE_TAP_SLOP_MULT = 2;

/** Read-only stick state for the §13 HUD (R8 draws the ring + knob). */
export interface StickView {
  active: boolean;
  /** Screen-space CSS px, relative to the game surface's top-left. */
  originX: number;
  originY: number;
  curX: number;
  curY: number;
  radius: number;
  /** Post-dead-zone magnitude, 0..1. */
  magnitude: number;
  /** Unit direction in screen space (y grows downward). */
  dirX: number;
  dirY: number;
}

export interface TouchView {
  readonly move: StickView;
  readonly aim: StickView;
  /** Pointer-follow target, live only in the `follow` scheme. */
  readonly follow: FollowView;
  readonly supported: boolean;
}

function makeStick(): StickView {
  return { active: false, originX: 0, originY: 0, curX: 0, curY: 0, radius: STICK_RADIUS_PX, magnitude: 0, dirX: 0, dirY: 0 };
}

/**
 * A touch-side stick. `rawX`/`rawY` are the unclamped travel vector in CSS px; the owner applies
 * the dead-zone remap so touch and gamepad share one curve.
 */
class Stick {
  pointerId = -1;
  view: StickView = makeStick();
  rawX = 0;
  rawY = 0;
  downTick = -1;
  travel = 0;
  radius = STICK_RADIUS_PX;
  /** Touch-down point, CSS px. Held separately from the view origin, which sticky-follows. */
  downX = 0;
  downY = 0;

  begin(id: number, x: number, y: number, tick: number): void {
    this.pointerId = id;
    this.view.active = true;
    this.view.originX = x;
    this.view.originY = y;
    this.view.curX = x;
    this.view.curY = y;
    this.view.radius = this.radius;
    this.view.magnitude = 0;
    this.view.dirX = 0;
    this.view.dirY = 0;
    this.rawX = 0;
    this.rawY = 0;
    this.downTick = tick;
    this.travel = 0;
    this.downX = x;
    this.downY = y;
  }

  move(x: number, y: number): void {
    const v = this.view;
    v.curX = x;
    v.curY = y;
    let dx = x - v.originX;
    let dy = y - v.originY;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > this.travel) this.travel = d;
    if (d > this.radius && d > 0) {
      // sticky-follow: drag the origin along so the stick never saturates out of reach
      const k = this.radius / d;
      v.originX = x - dx * k;
      v.originY = y - dy * k;
      dx *= k;
      dy *= k;
    }
    this.rawX = dx;
    this.rawY = dy;
  }

  end(): void {
    this.pointerId = -1;
    this.view.active = false;
    this.view.magnitude = 0;
    this.view.dirX = 0;
    this.view.dirY = 0;
    this.rawX = 0;
    this.rawY = 0;
    this.downTick = -1;
  }
}

export class TouchDevice {
  readonly moveStick = new Stick();
  readonly aimStick = new Stick();
  readonly follow = new FollowPointer();
  readonly view: TouchView;
  /** AMD-1 twin-stick, or whole-surface pointer-follow. Default `stick` — nothing changes silently. */
  scheme: MovementScheme = 'stick';
  /** Set on any touch-down so the manager can switch `activeDevice`. */
  activity = false;
  /** AMD-1 tunables, persisted in `settings.v1` at `input.touch`. */
  stickRadius = STICK_RADIUS_PX;
  tapTicks = TAP_TICKS;
  tapSlop = TAP_TRAVEL_PX;
  /** §3.6 double-tap gap, in sim ticks. Backed by `input.double_tap_ms`. */
  doubleTapTicks = DOUBLE_TAP_TICKS;
  /**
   * Inert since roles went by arrival order — there are no halves left to mirror. Kept so the
   * persisted `settings.v1` shape does not change under players who already have it stored.
   */
  leftHanded = false;
  /** Latched by the second touch-down of a double-tap; cleared by `consumeDashTap()`. */
  private dashTap = false;
  private dashTapTick = -1;
  /** Release tick of the tap waiting for a partner, and its own touch-down point. -1 = none. */
  private lastTapTick = -1;
  private lastTapX = 0;
  private lastTapY = 0;
  /** Pointer that closed a pair: its own release must not open the next one. -1 = none. */
  private pairedId = -1;
  private tick = 0;
  private rectLeft = 0;
  private rectTop = 0;
  private disposed = false;

  private readonly onDown: (ev: PointerEvent) => void;
  private readonly onMove: (ev: PointerEvent) => void;
  private readonly onUp: (ev: PointerEvent) => void;
  private readonly onCancel: (ev: PointerEvent) => void;
  private readonly onVisibility: () => void;
  private readonly onContextMenu: (ev: Event) => void;

  constructor(private target: HTMLElement) {
    this.view = {
      move: this.moveStick.view,
      aim: this.aimStick.view,
      follow: this.follow.view,
      supported: hasTouch(),
    };
    target.style.touchAction = 'none';
    target.style.userSelect = 'none';
    target.style.webkitUserSelect = 'none';
    (target.style as unknown as Record<string, string>).webkitTouchCallout = 'none';

    this.onDown = (ev) => this.handleDown(ev);
    this.onMove = (ev) => this.handleMove(ev);
    this.onUp = (ev) => this.handleUp(ev, true);
    this.onCancel = (ev) => this.handleUp(ev, false);
    this.onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') this.releaseAll();
    };
    this.onContextMenu = (ev) => ev.preventDefault();

    target.addEventListener('pointerdown', this.onDown, { passive: false });
    target.addEventListener('pointermove', this.onMove, { passive: false });
    target.addEventListener('pointerup', this.onUp, { passive: false });
    target.addEventListener('pointercancel', this.onCancel, { passive: false });
    target.addEventListener('lostpointercapture', this.onCancel, { passive: false });
    target.addEventListener('contextmenu', this.onContextMenu);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('blur', this.onVisibility);
  }

  setTick(tick: number): void {
    this.tick = tick;
  }

  /**
   * Switches the touch scheme. Any pointer held across the switch is dropped rather than
   * reinterpreted: a thumb that started as a stick origin means something else entirely as a
   * follow target, and carrying it over would fling the ship.
   */
  setScheme(scheme: MovementScheme): void {
    if (scheme === this.scheme) return;
    this.scheme = scheme;
    this.releaseAll();
  }

  /** Applies the persisted AMD-1 touch tuning. Safe to call at any time. */
  configure(stickRadius: number, tapMs: number, tapSlop: number, leftHanded: boolean, doubleTapMs = DOUBLE_TAP_MS): void {
    this.stickRadius = stickRadius;
    this.tapTicks = Math.max(1, Math.round(tapMs / (1000 / 60)));
    this.tapSlop = tapSlop;
    this.doubleTapTicks = Math.max(1, Math.round(doubleTapMs / (1000 / 60)));
    this.leftHanded = leftHanded;
    this.moveStick.radius = stickRadius;
    this.aimStick.radius = stickRadius;
    this.moveStick.view.radius = stickRadius;
    this.aimStick.view.radius = stickRadius;
  }

  /** True exactly once per double-tap (§3.4 dash, AMD-1's tap-to-dash amended). */
  consumeDashTap(): boolean {
    if (!this.dashTap) return false;
    this.dashTap = false;
    return true;
  }

  /** Sim tick the dash tap landed on, for §3.6 buffering. -1 when nothing is pending. */
  get dashTapAtTick(): number {
    return this.dashTapTick;
  }

  get moveActive(): boolean {
    return this.moveStick.view.active;
  }

  get aimActive(): boolean {
    return this.aimStick.view.active;
  }

  releaseAll(): void {
    this.moveStick.end();
    this.aimStick.end();
    this.follow.end();
    // half a gesture must not survive a pause, a blur or a scheme switch and fuse with the first
    // tap after it
    this.lastTapTick = -1;
    this.pairedId = -1;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const t = this.target;
    t.removeEventListener('pointerdown', this.onDown);
    t.removeEventListener('pointermove', this.onMove);
    t.removeEventListener('pointerup', this.onUp);
    t.removeEventListener('pointercancel', this.onCancel);
    t.removeEventListener('lostpointercapture', this.onCancel);
    t.removeEventListener('contextmenu', this.onContextMenu);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('blur', this.onVisibility);
    this.releaseAll();
  }

  private refreshRect(): void {
    const r = this.target.getBoundingClientRect();
    this.rectLeft = r.left;
    this.rectTop = r.top;
  }

  /** Test/headless hook: a touch-down at a surface-relative CSS px point. */
  injectDown(pointerId: number, x: number, y: number): void {
    this.refreshRect();
    this.applyDown(pointerId, x, y);
  }

  /** Test/headless hook: drag a tracked pointer. */
  injectMove(pointerId: number, x: number, y: number): void {
    this.applyMove(pointerId, x, y);
  }

  /** Test/headless hook: release a tracked pointer. `committed` false = pointercancel. */
  injectUp(pointerId: number, committed = true): void {
    this.applyUp(pointerId, committed);
  }

  private handleDown(ev: PointerEvent): void {
    if (ev.pointerType !== 'touch') return;
    if (isUiTarget(ev.target)) return; // never swallow a HUD button tap
    ev.preventDefault();
    this.refreshRect();
    if (!this.applyDown(ev.pointerId, ev.clientX - this.rectLeft, ev.clientY - this.rectTop)) return;
    try {
      this.target.setPointerCapture(ev.pointerId);
    } catch {
      /* capture is best-effort */
    }
  }

  private handleMove(ev: PointerEvent): void {
    if (ev.pointerType !== 'touch') return;
    if (!this.tracks(ev.pointerId)) return;
    ev.preventDefault();
    this.applyMove(ev.pointerId, ev.clientX - this.rectLeft, ev.clientY - this.rectTop);
  }

  private handleUp(ev: PointerEvent, committed: boolean): void {
    if (ev.pointerType !== 'touch') return;
    if (!this.tracks(ev.pointerId)) return;
    ev.preventDefault();
    this.applyUp(ev.pointerId, committed);
    try {
      this.target.releasePointerCapture(ev.pointerId);
    } catch {
      /* already released */
    }
  }

  /** Returns false when the touch found no free role and was dropped. */
  private applyDown(id: number, x: number, y: number): boolean {
    if (this.tracks(id)) return false;
    // Arrival order, not screen half, in both schemes: first finger moves, second aims, rest drop.
    // The role is decided here and only here, so it cannot change under the finger later.
    if (this.scheme === 'follow') {
      if (this.follow.pointerId === -1) this.follow.begin(id, x, y, this.tick);
      else if (this.aimStick.pointerId === -1) this.aimStick.begin(id, x, y, this.tick);
      else return false;
    } else if (this.moveStick.pointerId === -1) {
      this.moveStick.begin(id, x, y, this.tick);
    } else if (this.aimStick.pointerId === -1) {
      this.aimStick.begin(id, x, y, this.tick);
    } else {
      return false;
    }
    this.activity = true;
    this.closeDoubleTap(id, x, y);
    return true;
  }

  private applyMove(id: number, x: number, y: number): void {
    if (this.follow.pointerId === id) {
      this.follow.move(x, y);
      return;
    }
    const stick = this.stickFor(id);
    if (stick) stick.move(x, y);
  }

  private applyUp(id: number, committed: boolean): void {
    // Only the pointer that lifted is ended. The other role keeps its own pointer and its own
    // gesture: a lifted movement finger frees the movement slot, it does not promote the aim one.
    if (this.follow.pointerId === id) {
      if (committed) this.armDoubleTap(id, this.follow.downTick, this.follow.travel, this.follow.downX, this.follow.downY);
      this.follow.end();
      return;
    }
    const stick = this.stickFor(id);
    if (!stick) return;
    // either finger can arm a pair — dash belongs to the gesture, not to a role or a half
    if (committed) this.armDoubleTap(id, stick.downTick, stick.travel, stick.downX, stick.downY);
    stick.end();
  }

  /**
   * A committed release that qualifies as a §3.6 tap arms the first half of a double-tap. A release
   * that travelled too far or held too long is a drag and arms nothing, which is why an ordinary
   * aim drag can never become the opening beat of an accidental dash.
   *
   * A contact that already *closed* a pair cannot open the next one. Pairs therefore never overlap:
   * three taps are one dash, four are two, and holding a thumb down tapping does not machine-gun.
   */
  private armDoubleTap(id: number, downTick: number, travel: number, downX: number, downY: number): void {
    if (id === this.pairedId) {
      this.pairedId = -1;
      return;
    }
    if (downTick < 0) return;
    if (this.tick - downTick > this.tapTicks || travel >= this.tapSlop) return;
    this.lastTapTick = this.tick;
    this.lastTapX = downX;
    this.lastTapY = downY;
  }

  /**
   * Closes the pair on a touch-down that lands soon enough after, and near enough to, the armed
   * tap. Consumes the armed tap either way: a down that misses the gate starts a fresh pair rather
   * than leaving a stale one to fuse with a later, unrelated tap.
   */
  private closeDoubleTap(id: number, x: number, y: number): void {
    if (this.lastTapTick < 0) return;
    const gap = this.tick - this.lastTapTick;
    const dx = x - this.lastTapX;
    const dy = y - this.lastTapY;
    const slop = this.tapSlop * DOUBLE_TAP_SLOP_MULT;
    this.lastTapTick = -1;
    if (gap > this.doubleTapTicks || dx * dx + dy * dy > slop * slop) return;
    this.dashTap = true;
    this.dashTapTick = this.tick;
    this.pairedId = id;
  }

  private tracks(id: number): boolean {
    return this.follow.pointerId === id || this.moveStick.pointerId === id || this.aimStick.pointerId === id;
  }

  private stickFor(id: number): Stick | null {
    if (this.moveStick.pointerId === id) return this.moveStick;
    if (this.aimStick.pointerId === id) return this.aimStick;
    return null;
  }
}

/** UI elements opt out of stick capture by carrying `data-ui` (R8's HUD marks its own buttons). */
function isUiTarget(t: EventTarget | null): boolean {
  if (!t || typeof (t as Element).closest !== 'function') return false;
  return (t as Element).closest('[data-ui]') !== null;
}

export function hasTouch(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0) return true;
  return typeof window !== 'undefined' && 'ontouchstart' in window;
}
